import { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Sparkles, Crown, Check, Loader2, Gift } from "lucide-react";

export default function Subscription() {
    const { sub, refreshSub } = useOutletContext();
    const { user } = useAuth();
    const [plans, setPlans] = useState([]);
    const [busy, setBusy] = useState(null);
    const welcomeEligible = !!user?.welcome_bonus_eligible && sub?.status !== "active";
    const creditCents = Number(user?.account_credit_cents || 0);
    const creditDisplay = (creditCents / 100).toFixed(2);

    useEffect(() => {
        api.get("/plans")
            .then((r) => setPlans(r.data))
            .catch((err) => console.warn("plans fetch failed:", err?.message || err));
    }, []);

    const startCheckout = async (planId) => {
        setBusy(planId);
        try {
            const { data } = await api.post("/checkout/session", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            if (data?.url) {
                window.location.href = data.url;
            } else {
                toast.error("Checkout failed");
                setBusy(null);
            }
        } catch (err) {
            console.error("checkout failed:", err);
            toast.error("Checkout failed");
            setBusy(null);
        }
    };

    const buttonLabel = (plan) => {
        const isCurrent = sub?.plan === plan.id && sub?.status === "active";
        if (isCurrent) return "Extend by 30 days";
        if (welcomeEligible) return `Claim Bonus — $${(plan.price / 2).toFixed(2)}`;
        return `Subscribe — $${plan.price}`;
    };

    return (
        <div className="max-w-[1200px] mx-auto space-y-10">
            <div>
                <p className="text-xs tracking-[0.3em] uppercase text-rose">Subscription</p>
                <h1 className="font-serif text-4xl sm:text-5xl mt-2">Manage your plan</h1>
            </div>

            {welcomeEligible && (
                <Link
                    to="/welcome"
                    data-testid="welcome-bonus-banner"
                    className="block rounded-3xl p-6 border border-gold/40 bg-gradient-to-r from-gold/15 via-rose/10 to-transparent hover:border-gold/70 transition group"
                >
                    <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-full bg-gold/20 border border-gold/40 flex items-center justify-center shrink-0">
                            <Gift className="w-5 h-5 text-gold" />
                        </div>
                        <div className="flex-1 min-w-0">
                            <p className="text-xs tracking-[0.3em] uppercase text-gold">✦ Welcome bonus</p>
                            <p className="font-serif text-2xl mt-1">Your first month is 50% off</p>
                            <p className="text-sm text-white/60 mt-1">Auto-applied at checkout. One-time only.</p>
                        </div>
                        <span className="hidden sm:inline-block text-rose group-hover:translate-x-1 transition">→</span>
                    </div>
                </Link>
            )}

            {creditCents > 0 && (
                <div data-testid="credit-balance" className="rounded-3xl p-6 border border-rose/40 bg-rose/5 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-rose/15 border border-rose/40 flex items-center justify-center shrink-0">
                        <Gift className="w-5 h-5 text-rose" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <p className="text-xs tracking-[0.3em] uppercase text-rose">Account credit</p>
                        <p className="font-serif text-2xl mt-1">${creditDisplay} ready to use</p>
                        <p className="text-sm text-white/60 mt-1">Auto-applied to your next subscription charge.</p>
                    </div>
                    <Link to="/portal/referrals" className="text-rose text-sm hover:underline shrink-0">Friend Pass →</Link>
                </div>
            )}

            {/* Current */}
            <div className="rounded-3xl border border-white/10 bg-surface p-8 grid md:grid-cols-3 gap-6">
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-white/50">Current status</p>
                    <p className="font-serif text-3xl mt-2 capitalize">{sub?.status || "Inactive"}</p>
                </div>
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-white/50">Plan</p>
                    <p className="font-serif text-3xl mt-2 capitalize">{sub?.plan || "—"}</p>
                </div>
                <div>
                    <p className="text-xs tracking-[0.3em] uppercase text-white/50">Days remaining</p>
                    <p className="font-serif text-3xl mt-2 text-rose">{sub?.days_remaining ?? 0}</p>
                </div>
            </div>

            {/* Choose plan */}
            <div>
                <h2 className="font-serif text-3xl">{sub?.status === "active" ? "Renew or upgrade" : "Activate your plan"}</h2>
                <p className="text-white/60 mt-2">Each payment extends your access by 30 days. Stop anytime — no contract.</p>

                <div className="mt-8 grid md:grid-cols-2 gap-6">
                    {plans.map((p) => {
                        const isAcrylic = p.id === "acrylic";
                        return (
                            <div
                                key={p.id}
                                data-testid={`sub-plan-${p.id}`}
                                className={`relative rounded-3xl p-8 ${
                                    isAcrylic ? "tracing-beam" : "bg-surface border border-white/10"
                                }`}
                            >
                                {isAcrylic && (
                                    <span className="absolute -top-3 left-8 px-3 py-1 rounded-full bg-gold text-ink text-xs tracking-widest uppercase">
                                        Most Popular
                                    </span>
                                )}
                                <div className="flex items-center gap-2">
                                    {isAcrylic ? <Crown className="w-5 h-5 text-gold" /> : <Sparkles className="w-5 h-5 text-rose" />}
                                    <p className="text-xs tracking-[0.3em] uppercase text-white/60">{p.name}</p>
                                </div>
                                <div className="mt-4 flex items-baseline gap-2">
                                    {welcomeEligible && (
                                        <span className="font-serif text-3xl text-white/35 line-through">${p.price}</span>
                                    )}
                                    <span className="font-serif text-6xl">${welcomeEligible ? (p.price / 2).toFixed(2) : p.price}</span>
                                    <span className="text-white/50 mb-3">/ 30 days</span>
                                </div>
                                {welcomeEligible && (
                                    <p className="text-xs text-gold mt-1">✦ First month — 50% off applied automatically</p>
                                )}
                                <ul className="mt-6 space-y-2 text-sm text-white/75">
                                    {(isAcrylic
                                        ? [
                                              "1 full acrylic set per month",
                                              "Unlimited fill-ins all month",
                                              "AI 3D nail art (3,400 DPI)",
                                              "Same-day · book before 6pm",
                                              "Emergency in under an hour (small upcharge)",
                                              "Mobile — we come to you",
                                          ]
                                        : [
                                              "Unlimited patent-pending gel sets",
                                              "Lasts 7–14 days · UV-cured",
                                              "Pro prep: buff · drill · file · bond",
                                              "AI 3D nail art (3,400 DPI)",
                                              "Same-day · book before 6pm",
                                              "Emergency in under an hour (small upcharge)",
                                          ]
                                    ).map((f) => (
                                        <li key={f} className="flex gap-2">
                                            <Check className="w-4 h-4 text-rose mt-0.5" /> <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                <button
                                    data-testid={`checkout-${p.id}`}
                                    onClick={() => startCheckout(p.id)}
                                    disabled={busy === p.id}
                                    className={`mt-8 w-full px-6 py-3 rounded-full transition font-medium flex items-center justify-center gap-2 ${
                                        isAcrylic
                                            ? "bg-rose text-ink hover:bg-roseHover"
                                            : "border border-rose text-rose hover:bg-rose hover:text-ink"
                                    } disabled:opacity-50`}
                                >
                                    {busy === p.id ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                                    {buttonLabel(p)}
                                </button>
                            </div>
                        );
                    })}
                </div>

                <p className="mt-6 text-xs text-white/40 text-center">
                    Payments are processed securely via Stripe. Each charge extends your subscription period by 30 days.
                </p>
            </div>
            {refreshSub ? null : null}
        </div>
    );
}
